/**
 * 밴드QR 출력 페이지 (테스트 서버 전용)
 *
 * 매수(1~50)를 입력하고 버튼을 누르면 서명 없는 랜덤 밴드코드
 * (ZLB_{12}_{16})를 그 수만큼 생성해 USB 프린터로 출력한다.
 * DB(qr_bands)에는 기록하지 않는다 — 등록은 고객이 앱으로 스캔할 때 TOFU.
 *
 * ⚠️ QR 에 실리는 값은 코드 원문이 아니라 웹앱 진입 URL(`…/bands/{코드}`)이다.
 *    화면의 "생성된 코드" 목록은 원문이라 인쇄물과 문자열이 다르다(대조는 주소 끝 코드로).
 *
 * 설계: docs/2026-07-15-tools-band-qr-print-design.md v1.1 / dev-steps S5
 *      docs/2026-07-22-band-qr-url-format-migration-dev-steps.md S6 (URL 전환)
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { printBandQr } from '../services/api';
import './BandQrPrintPage.css';

const MIN_COUNT = 1;
const MAX_COUNT = 50;

export default function BandQrPrintPage() {
  const navigate = useNavigate();
  const [count, setCount] = useState(10);
  const [printing, setPrinting] = useState(false);
  const [result, setResult] = useState(null); // { success, message, codes? }

  const handlePrint = useCallback(async () => {
    const n = Number(count);
    if (!Number.isInteger(n) || n < MIN_COUNT || n > MAX_COUNT) {
      setResult({ success: false, message: `매수는 ${MIN_COUNT}~${MAX_COUNT} 사이의 정수여야 합니다` });
      return;
    }

    setPrinting(true);
    setResult(null);
    try {
      const res = await printBandQr(n);
      if (res.success === false) {
        setResult({ success: false, message: res.message || '출력에 실패했습니다' });
      } else {
        setResult({
          success: true,
          message: res.message || `밴드QR ${res.printed_count}장 출력 완료`,
          codes: res.codes || [],
        });
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '출력 중 오류가 발생했습니다';
      setResult({ success: false, message: msg });
    } finally {
      setPrinting(false);
    }
  }, [count]);

  return (
    <div className="band-qr-print-page">
      <header className="bqp-header">
        <button className="bqp-back" onClick={() => navigate('/')}>← 메뉴</button>
        <h1>밴드QR 출력</h1>
        <span className="bqp-badge">TEST 전용</span>
      </header>

      <div className="bqp-body">
        <label className="bqp-label" htmlFor="bqp-count">출력 매수 (1~50)</label>
        <input
          id="bqp-count"
          className="bqp-count-input"
          type="number"
          min={MIN_COUNT}
          max={MAX_COUNT}
          value={count}
          onChange={(e) => setCount(e.target.value)}
          disabled={printing}
        />

        <button
          className="bqp-print-btn"
          onClick={handlePrint}
          disabled={printing}
        >
          {printing ? '출력 중…' : '밴드QR 출력'}
        </button>

        {result && (
          <div className={`bqp-result ${result.success ? 'bqp-ok' : 'bqp-err'}`}>
            <p className="bqp-result-msg">{result.message}</p>
            {result.success && result.codes?.length > 0 && (
              <details className="bqp-codes">
                <summary>생성된 코드 {result.codes.length}개 (현장 대조용 · 인쇄 QR 은 URL)</summary>
                <p>
                  ⚠️ 아래 목록은 <b>밴드 코드 원문</b>입니다. 실제 인쇄된 QR 에는
                  웹앱 주소(<code>…/bands/{'{코드}'}</code>)가 들어 있어, 폰 카메라로 스캔하면
                  이 문자열이 아니라 주소가 보입니다. 대조는 <b>주소 끝의 코드</b>로 하세요.
                </p>
                <ul>
                  {result.codes.map((c) => <li key={c}>{c}</li>)}
                </ul>
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
